import { useIntl } from 'react-intl';

import { editorCardProvider } from '@atlaskit/editor-card-provider';
import { type PublicPluginAPI } from '@atlaskit/editor-common/types';
import { createDefaultPreset } from '@atlaskit/editor-core/preset-default';
import { usePreset } from '@atlaskit/editor-core/use-preset';
import { cardPlugin, type CardPlugin } from '@atlaskit/editor-plugin-card';
import { type FocusPlugin } from '@atlaskit/editor-plugin-focus';
import { gridPlugin, type GridPlugin } from '@atlaskit/editor-plugin-grid';
import { listPlugin, type ListPlugin } from '@atlaskit/editor-plugin-list';
import {
	maxContentSizePlugin,
	type MaxContentSizePlugin,
} from '@atlaskit/editor-plugin-max-content-size';
import { mentionsPlugin, type MentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import type { QuickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';

import messages from './messages';

/**
 * The maximum number of characters that can be entered in the chat input.
 */
export const MAX_CHARACTERS_INPUT = 16000;

type EditorPresetProps = {
	productKey: string;
	siteId: string;
};

export const useEditorPreset = ({
	productKey,
	siteId,
}: EditorPresetProps): {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	preset: any;
	/**
	 * This type needs to be defined explicitly otherwise we get deep import errors.
	 * This is a non-exhaustive list of plugins that are used in our preset - there are more default plugins but we want to avoid adding these to package.json if they are not used.
	 */
	editorApi?: PublicPluginAPI<
		[
			CardPlugin,
			MaxContentSizePlugin,
			MentionsPlugin,
			GridPlugin,
			QuickInsertPlugin,
			ListPlugin,
			FocusPlugin,
		]
	>;
} => {
	const { formatMessage } = useIntl();
	const linkPicker = useLinkPickerEditorProps(undefined, {
		cloudId: siteId,
		product: productKey,
	});

	const { preset, editorApi } = usePreset(() => {
		return createDefaultPreset({
			placeholder: {
				placeholder: formatMessage(messages.jiraIssueCreateDescriptionEditorPlaceholder),
			},
			hyperlinkOptions: {
				linkPicker,
			},
			blockType: {
				allowBlockType: {
					exclude: ['heading'],
				},
			},
		})
			.add([maxContentSizePlugin, MAX_CHARACTERS_INPUT])
			.add(mentionsPlugin)
			.add(gridPlugin)
			.add([
				cardPlugin,
				{
					allowBlockCards: false,
					allowEmbeds: false,
					linkPicker,
					provider: Promise.resolve(editorCardProvider),
				},
			])
			.add(listPlugin);
	}, []);

	return {
		preset,
		editorApi,
	};
};
